import React, { useEffect } from 'react';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useFormik } from 'formik';
import { createSchema, editSchema } from './demoSchema';

const EMPTY_FORM = { id: '', title: '', url: '', description: '' };

const DemoFormDialog = ({ open, onClose, onSave, initial, saving }) => {
  const isEdit = Boolean(initial);

  const formik = useFormik({
    initialValues: initial ?? EMPTY_FORM,
    validationSchema: isEdit ? editSchema : createSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: (values) => onSave(values),
  });

  useEffect(() => {
    if (!open) formik.resetForm();
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
        {isEdit ? 'Edit Demo' : 'Add Demo'}
        <IconButton size="small" onClick={onClose} disabled={saving}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        <TextField
          label="Demo ID"
          name="id"
          value={formik.values.id}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          disabled={isEdit}
          required={!isEdit}
          fullWidth
          placeholder="e.g. product-tour-v2"
          error={formik.touched.id && Boolean(formik.errors.id)}
          helperText={
            isEdit
              ? 'ID cannot be changed'
              : (formik.touched.id && formik.errors.id) || 'Unique identifier for this demo'
          }
        />
        <TextField
          label="Title"
          name="title"
          value={formik.values.title}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          required
          fullWidth
          placeholder="e.g. Intelliconvo Product Tour"
          error={formik.touched.title && Boolean(formik.errors.title)}
          helperText={formik.touched.title && formik.errors.title}
        />
        <TextField
          label="Demo URL"
          name="url"
          value={formik.values.url}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          required
          fullWidth
          placeholder="https://app.storylane.io/share/..."
          error={formik.touched.url && Boolean(formik.errors.url)}
          helperText={formik.touched.url && formik.errors.url}
        />
        <TextField
          label="Description"
          name="description"
          value={formik.values.description}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          fullWidth
          multiline
          rows={2}
          error={formik.touched.description && Boolean(formik.errors.description)}
          helperText={formik.touched.description && formik.errors.description}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!formik.isValid || saving}
          onClick={formik.submitForm}
        >
          {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Demo'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DemoFormDialog;
